
import type { NotificationEventType } from '@/entities/notification';

interface Note {
  freq: number;
  at: number;
  dur: number;
}

interface Chime {
  notes: Note[];
  wave: OscillatorType;
}

const A5 = 880;
const Cs6 = 1108.73;
const E6 = 1318.51;
const E5 = 659.25;
const Fs5 = 739.99;
const B4 = 493.88;

const CHIMES = {
  message: {
    wave: 'sine',
    notes: [
      { freq: E5, at: 0, dur: 0.18 },
      { freq: A5, at: 0.1, dur: 0.3 },
    ],
  },
  created: {
    wave: 'triangle',
    notes: [
      { freq: A5, at: 0, dur: 0.15 },
      { freq: Cs6, at: 0.09, dur: 0.15 },
      { freq: E6, at: 0.18, dur: 0.35 },
    ],
  },
  status: {
    wave: 'sine',
    notes: [{ freq: A5, at: 0, dur: 0.35 }],
  },
  attention: {
    wave: 'triangle',
    notes: [
      { freq: Cs6, at: 0, dur: 0.12 },
      { freq: Cs6, at: 0.16, dur: 0.25 },
    ],
  },
  positive: {
    wave: 'sine',
    notes: [
      { freq: A5, at: 0, dur: 0.15 },
      { freq: E6, at: 0.11, dur: 0.3 },
    ],
  },
  mention: {
    wave: 'triangle',
    notes: [
      { freq: E5, at: 0, dur: 0.12 },
      { freq: A5, at: 0.1, dur: 0.12 },
      { freq: E6, at: 0.2, dur: 0.4 },
    ],
  },
  negative: {
    wave: 'sine',
    notes: [
      { freq: Fs5, at: 0, dur: 0.16 },
      { freq: B4, at: 0.12, dur: 0.32 },
    ],
  },
} satisfies Record<string, Chime>;

export type ChimeKind = keyof typeof CHIMES;

const EVENT_CHIME: Record<NotificationEventType, ChimeKind> = {
  message_sent: 'message',
  ticket_created: 'created',
  status_changed: 'status',
  participant_added: 'status',
  participant_removed: 'negative',
  assignee_added: 'positive',
  assignee_removed: 'negative',
  assignee_reassigned: 'status',
  approval_created: 'attention',
  approval_approved: 'positive',
  approval_rejected: 'negative',
  approval_level_reminded: 'attention',
  folder_grant_expired: 'attention',
  approval_round_approved: 'positive',
  approval_round_rejected: 'negative',
  mentioned: 'mention',
  task_assigned: 'positive',
  task_reopened: 'attention',
  task_ticket_finished: 'status',
  task_commented: 'status',
  task_mentioned: 'mention',
  sprint_started: 'status',
  sprint_closed: 'positive',
  sprint_overdue: 'attention',
  system_status: 'attention',
  chat_mentioned: 'mention',
  chat_message: 'message',
};

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  return ctx;
}

let unlockInstalled = false;

export function installSoundUnlock(): void {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const events = ['pointerdown', 'keydown', 'touchend'] as const;
  const cleanup = () => {
    for (const e of events) window.removeEventListener(e, unlock);
  };
  const unlock = () => {
    const ac = getContext();
    if (!ac) {
      cleanup();
      return;
    }
    if (ac.state === 'suspended') void ac.resume().catch(() => undefined);
    try {
      const src = ac.createBufferSource();
      src.buffer = ac.createBuffer(1, 1, 22050);
      src.connect(ac.destination);
      src.start(0);
    } catch {
    }
    if (ac.state === 'running') cleanup();
  };
  for (const e of events) window.addEventListener(e, unlock, { passive: true });
}

function playChime(chime: Chime, volume: number): void {
  const ac = getContext();
  if (!ac || volume <= 0) return;
  const start = () => {
    const t0 = ac.currentTime;
    for (const note of chime.notes) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = chime.wave;
      osc.frequency.value = note.freq;
      gain.gain.setValueAtTime(0.0001, t0 + note.at);
      gain.gain.exponentialRampToValueAtTime(volume * 0.25, t0 + note.at + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + note.at + note.dur);
      osc.connect(gain).connect(ac.destination);
      osc.start(t0 + note.at);
      osc.stop(t0 + note.at + note.dur + 0.05);
    }
  };
  if (ac.state === 'suspended') {
    ac.resume().then(() => {
      if (ac.state === 'running') start();
    }).catch(() => undefined);
    return;
  }
  start();
}

export function playNotificationSound(
  eventType: string,
  volume: number,
  payload?: Record<string, unknown> | null,
): void {
  const resolved =
    eventType === 'task_reopened' && payload?.reason === 'ticket_finished' ? 'task_ticket_finished' : eventType;
  const kind = EVENT_CHIME[resolved as NotificationEventType] ?? 'status';
  playChime(CHIMES[kind], volume);
}

export function playMentionSound(volume: number): void {
  playChime(CHIMES.mention, Math.max(volume, 0.4));
}

export function playPreviewSound(volume: number): void {
  playChime(CHIMES.message, volume);
}
